import { Injectable, RendererFactory2 } from '@angular/core';

/**
 * Ensures that every <style> element injected by Angular during runtime carries the CSP nonce
 * provided by the server. The nonce is looked up in the following order:
 *   1) <meta name="csp-nonce" content="..."> inside <head>
 *   2) ngCspNonce attribute on the application root element (added in index.html)
 */
@Injectable({ providedIn: 'root' })
export class CspNonceService {
  constructor(rendererFactory: RendererFactory2) {
    const nonce = this._resolveNonce();
    if (!nonce) {
      return;
    }

    this.#noone = nonce;
    // Add the nonce attribute to any style tags that already exist without it
    Array.from(document.querySelectorAll('style:not([nonce])')).forEach((el: Element) => {
      (el as HTMLElement).setAttribute('nonce', nonce);
    });

    // Observe future <style> tag insertions and patch them
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && (node as HTMLElement).tagName === 'STYLE') {
            (node as HTMLElement).setAttribute('nonce', nonce);
          }
        });
      });
    });

    observer.observe(document.head || document.documentElement, { childList: true, subtree: true });
  }

  #noone: string | null = null;

  get nonce(): string | null {
    return this.#noone;
  }
  
  private _resolveNonce(): string | null {
    const meta = document.querySelector('meta[name="csp-nonce"]');
    if (meta) {
      const value = meta.getAttribute('content');
      if (value) {
        return value;
      }
    }
    const root = document.querySelector('[ngCspNonce]');
    if (root) {
      const value = root.getAttribute('ngCspNonce');
      if (value) {
        return value;
      }
    }
    return null;
  }

} 